<template>
  <span
    class="report-rebuild-slot"
    :class="{ 'is-active': busy || tracking || error }"
  >
    <b-button
      v-if="!status || status.canRequest || busy || tracking || error"
      variant="flat-secondary"
      class="report-rebuild-button"
      :disabled="busy || tracking"
      :title="label"
      :aria-label="`${label}: ${huayName}`"
      :aria-busy="busy || tracking ? 'true' : 'false'"
      @focus="prepare"
      @click="requestBuild"
    >
      <feather-icon
        icon="RefreshCwIcon"
        size="15"
        :class="{ 'report-rebuild-spin': busy || tracking }"
      />
    </b-button>
  </span>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import lottoReportService from '@/service/lottoReportService'

export default {
  components: { BButton },
  props: {
    huayId: { type: String, required: true },
    roundId: { type: String, required: true },
    huayName: { type: String, required: true },
  },
  data() {
    return {
      status: null,
      busy: false,
      tracking: false,
      requested: false,
      error: '',
      statusRequest: null,
      pollTimer: null,
      disposed: false,
    }
  },
  computed: {
    canManage() {
      return this.$store.state.app.isMaster(this.$store) && this.$can('manage', 'all')
    },
    label() {
      if (this.error) return this.error
      if (this.busy) return this.$t('กำลังส่งคำสั่ง')
      if (this.tracking) {
        return this.$t(this.status && this.status.status === 'queued'
          ? 'รอเริ่มสร้างรายงาน' : 'กำลังสร้างรายงาน')
      }
      return this.$t('คำนวณรายงานรอบนี้ใหม่')
    },
  },
  beforeDestroy() {
    this.disposed = true
    clearTimeout(this.pollTimer)
  },
  methods: {
    async prepare() {
      if (!this.canManage || this.disposed || this.busy || this.status || this.tracking) return
      await this.loadStatus()
      this.schedulePoll()
    },
    async loadStatus() {
      if (this.disposed || !this.canManage) return null
      if (this.statusRequest) return this.statusRequest
      this.statusRequest = this.fetchStatus()
      try {
        return await this.statusRequest
      } finally {
        this.statusRequest = null
      }
    },
    async fetchStatus() {
      try {
        const res = await lottoReportService.getBuildStatus(this.huayId, this.roundId)
        if (this.disposed) return null
        if ([400, 403, 404].includes(res.status)) {
          this.status = { canRequest: false }
          this.tracking = false
          this.error = ''
          return this.status
        }
        if (res.status !== 200) throw new Error('status unavailable')
        this.status = res.data
        this.error = ''
        this.tracking = ['queued', 'running'].includes(res.data.status)
        if (this.requested && !this.tracking) {
          if (['failed', 'stalled'].includes(res.data.status)) {
            this.error = res.data.error || this.$t(res.data.status === 'stalled'
              ? 'งานสร้างรายงานไม่มีความคืบหน้า' : 'สร้างรายงานไม่สำเร็จ')
            this.notify(this.error, 'warning')
          }
          this.requested = false
        }
        return this.status
      } catch (error) {
        if (!this.disposed) this.error = this.$t('ไม่สามารถติดตามสถานะรายงานได้ กำลังลองเชื่อมต่ออีกครั้ง')
        return null
      }
    },
    schedulePoll() {
      clearTimeout(this.pollTimer)
      if (this.disposed || !this.canManage || (!this.tracking && !this.error)) return
      this.pollTimer = setTimeout(async () => {
        await this.loadStatus()
        this.schedulePoll()
      }, 3000)
    },
    async requestBuild() {
      if (!this.canManage || this.disposed || this.busy || this.tracking) return
      this.busy = true
      this.error = ''
      clearTimeout(this.pollTimer)
      try {
        const status = await this.loadStatus()
        if (this.disposed || !this.canManage) return
        if (!status) {
          this.notify(this.error, 'warning')
          return
        }
        if (!status.canRequest) {
          if (!this.tracking) this.notify(this.$t('ไม่สามารถคำนวณรายงานรอบนี้ใหม่ได้ในขณะนี้'), 'warning')
          return
        }
        this.requested = true
        const res = await lottoReportService.requestBuild(this.huayId, this.roundId)
        if (this.disposed) return
        if (res.status === 200 || res.status === 202) {
          this.status = { ...status, status: 'queued', canRequest: false }
          this.tracking = true
          this.notify(this.$t('ส่งคำสั่งคำนวณรายงานแล้ว'), 'success')
        } else {
          this.requested = false
          this.error = (res.data && res.data.message) || this.$t('ส่งคำสั่งสร้างรายงานไม่สำเร็จ กรุณาลองใหม่')
          this.notify(this.error, 'warning')
        }
      } catch (error) {
        if (!this.disposed) {
          this.tracking = true
          this.error = this.$t('ยังยืนยันการส่งคำสั่งไม่ได้ กำลังตรวจสอบสถานะงาน')
          this.notify(this.error, 'warning')
        }
      } finally {
        if (!this.disposed) {
          this.busy = false
          this.schedulePoll()
        }
      }
    },
    notify(message, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.huayName,
          text: message,
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.report-rebuild-slot {
  display: inline-flex;
  flex: 0 0 2rem;
  margin-left: 0.35rem;
  width: 2rem;
}
.report-rebuild-button {
  align-items: center;
  border: 0;
  display: inline-flex;
  height: 2rem;
  justify-content: center;
  padding: 0.45rem;
  width: 2rem;
}
.report-rebuild-button:disabled {
  opacity: 1;
}
.report-rebuild-spin {
  animation: report-rebuild-spin 1s linear infinite;
}
@keyframes report-rebuild-spin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .report-rebuild-spin {
    animation: none;
  }
}
</style>
